import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { getLanguageFromToken } from '@/lib/jwt-utils';

type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [language, setLanguage] = useState<Language>('en'); // Default to English

  // Determine language from JWT token when token changes
  useEffect(() => {
    if (token) {
      const tokenLanguage = getLanguageFromToken(token);
      setLanguage(tokenLanguage);
    } else {
      // No token available, keep English as default
      setLanguage('en');
    }
  }, [token]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
} 